"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { HeartPulse, Skull, Activity, ShieldAlert, TrendingUp, CircleX, CircleCheck, ArrowRight, Stethoscope, Accessibility, PiggyBank } from "lucide-react";
const scenarios = [{
  id: "critical-illness",
  icon: Stethoscope,
  label: "Critical Illness",
  traditional: {
    title: "Traditional Life Insurance",
    subtitle: "Death Benefit Only",
    description: "A severe health event like a heart attack, stroke, or cancer diagnosis occurs.",
    points: ["Policy offers zero financial support while you are alive.", "Medical bills and lost income drain your savings.", "Family faces immediate financial hardship during your recovery.", "Payout only happens upon death."]
  },
  modern: {
    title: "Modern Life Insurance",
    subtitle: "Living Benefits Included",
    description: "A severe health event like a heart attack, stroke, or cancer diagnosis occurs.",
    points: ["Accelerate a portion of your death benefit immediately.", "Receive tax-free cash to pay for experimental treatments.", "Replace lost income so you can focus 100% on recovery.", "Remaining benefit still protects your family."]
  }
}, {
  id: "chronic-illness",
  icon: Accessibility,
  label: "Chronic Illness",
  traditional: {
    title: "Traditional Life Insurance",
    subtitle: "Death Benefit Only",
    description: "You become unable to perform 2 of 6 Activities of Daily Living (ADLs).",
    points: ["No access to policy funds for long-term care needs.", "Forced to liquidate retirement assets to pay for nursing facilities.", "Burden of care often falls heavily on untrained family members.", "Wealth is depleted before death occurs."]
  },
  modern: {
    title: "Modern Life Insurance",
    subtitle: "Living Benefits Included",
    description: "You become unable to perform 2 of 6 Activities of Daily Living (ADLs).",
    points: ["Trigger the Chronic Illness rider to access funds tax-free.", "Pay for professional in-home care or facility expenses.", "Make necessary home modifications for accessibility.", "Preserve your other retirement assets and family's peace of mind."]
  }
}, {
  id: "retirement",
  icon: PiggyBank,
  label: "Retirement Supplement",
  traditional: {
    title: "Traditional Term Life",
    subtitle: "Pure Protection",
    description: "You reach retirement age healthy and no longer need massive death protection.",
    points: ["Term policy expires with zero return on premiums paid.", "No cash value accumulation to supplement retirement income.", "Must rely solely on 401k, IRA, or scarce pensions.", "Subject to market volatility and sequence of returns risk."]
  },
  modern: {
    title: "Hybrid Annuities & IUL",
    subtitle: "Wealth Accumulation",
    description: "You reach retirement age and want to maximize your income streams.",
    points: ["Access accumulated cash value via tax-advantaged loans.", "Create a self-made pension with Lifetime Guaranteed Income.", "Enjoy downside protection against market crashes.", "Participate in market upside with indexed strategies."]
  }
}];
export default function InsuranceForTheLiving() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="insurance-for-the-living" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[var(--chart-2)]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Badge variant="outline" className="mb-4 border-[var(--chart-2)] text-[var(--chart-2)]">
            The New Wave of Life Insurance
          </Badge>
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 tracking-tight">
            Insurance for the <span className="text-[var(--chart-2)]">Living</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Life insurance is no longer just about the financial impact of losing a loved one. 
            Modern policies include Accelerated Death Benefit riders that allow you to use your benefits today.
          </p>
        </div>

        <div ref={contentRef} className={`transition-all duration-1000 delay-200 ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Tabs defaultValue={scenarios[0].id} className="w-full flex flex-col items-center">
            <TabsList className="flex flex-wrap justify-center h-auto gap-2 bg-muted/50 p-1.5 rounded-xl mb-12 max-w-full overflow-x-auto">
              {scenarios.map((scenario, index) => {
              const Icon = scenario.icon;
              return <TabsTrigger key={scenario.id} value={scenario.id} data-index={index} className="px-4 py-2.5 rounded-lg data-[state=active]:bg-[var(--chart-2)]/10 data-[state=active]:text-[var(--chart-2)] data-[state=active]:shadow-none transition-all">
                    <Icon className="w-4 h-4 mr-2" />
                    {scenario.label}
                  </TabsTrigger>;
            })}
            </TabsList>

            <div className="w-full">
              {scenarios.map((scenario, index) => <TabsContent key={scenario.id} value={scenario.id} data-index={index} className="mt-0 outline-none focus-visible:ring-0">
                  <div className="grid lg:grid-cols-2 gap-8 items-stretch">
                    
                    {}
                    <Card className="bg-muted/20 border-border/40 opacity-80 grayscale-[0.5] transition-all hover:grayscale-0 hover:opacity-100 flex flex-col">
                      <CardHeader className="pb-4">
                        <div className="flex justify-between items-start mb-4">
                          <div className="p-3 bg-muted rounded-lg">
                            <Skull className="w-6 h-6 text-muted-foreground" />
                          </div>
                          <Badge variant="secondary" className="bg-muted text-muted-foreground">Outdated</Badge>
                        </div>
                        <CardTitle className="text-2xl text-muted-foreground">{scenario.traditional.title}</CardTitle>
                        <CardDescription className="text-base">{scenario.traditional.subtitle}</CardDescription>
                      </CardHeader>
                      <CardContent className="flex-1">
                        <div className="bg-background/50 p-4 rounded-lg mb-6 border border-border/50">
                          <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4" />
                            Scenario: {scenario.traditional.description}
                          </p>
                        </div>
                        <Separator className="mb-6 opacity-50" />
                        <ul className="space-y-4">
                          {scenario.traditional.points.map((point, ptIndex) => <li key={ptIndex} data-index={ptIndex} contentEditable={ptIndex === 1} suppressContentEditableWarning={ptIndex === 1} className="flex items-start gap-3 text-muted-foreground">
                              <CircleX className="w-5 h-5 shrink-0 text-destructive/70 mt-0.5" />
                              <span className="text-sm leading-relaxed">{point}</span>
                            </li>)}
                        </ul>
                      </CardContent>
                    </Card>

                    {}
                    <Card className="bg-card border-[var(--chart-2)]/50 shadow-[0_0_40px_-15px_var(--chart-2)] relative overflow-hidden flex flex-col group">
                      <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--chart-2)]/10 rounded-full blur-3xl -mr-20 -mt-20 transition-all group-hover:bg-[var(--chart-2)]/20" />
                      
                      <CardHeader className="pb-4 relative z-10">
                        <div className="flex justify-between items-start mb-4">
                          <div className="p-3 bg-[var(--chart-2)]/10 rounded-lg border border-[var(--chart-2)]/20">
                            <HeartPulse className="w-6 h-6 text-[var(--chart-2)]" />
                          </div>
                          <Badge className="bg-[var(--chart-2)] text-[var(--chart-2)] bg-opacity-10 hover:bg-opacity-20 border-[var(--chart-2)]/30">
                            Recommended
                          </Badge>
                        </div>
                        <CardTitle className="text-2xl text-foreground">{scenario.modern.title}</CardTitle>
                        <CardDescription className="text-base text-[var(--chart-2)] font-medium">
                          {scenario.modern.subtitle}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex-1 relative z-10">
                        <div className="bg-[var(--chart-2)]/5 p-4 rounded-lg mb-6 border border-[var(--chart-2)]/20">
                          <p className="text-sm font-medium text-foreground flex items-center gap-2">
                            <Activity className="w-4 h-4 text-[var(--chart-2)]" />
                            Scenario: {scenario.modern.description}
                          </p>
                        </div>
                        <Separator className="mb-6 bg-[var(--chart-2)]/20" />
                        <ul className="space-y-4">
                          {scenario.modern.points.map((point, ptIndex) => <li key={ptIndex} data-index={ptIndex} contentEditable={ptIndex === 1} suppressContentEditableWarning={ptIndex === 1} className="flex items-start gap-3 text-foreground">
                              <CircleCheck className="w-5 h-5 shrink-0 text-[var(--chart-2)] mt-0.5" />
                              <span className="text-sm leading-relaxed">{point}</span>
                            </li>)}
                        </ul>
                      </CardContent>
                      <CardFooter className="pt-6 relative z-10">
                        <Button asChild className="w-full bg-[var(--chart-2)] text-black hover:bg-[var(--chart-2)]/90 font-semibold">
                          <Link to="/LifeInsurancePage">
                            Explore Living Benefits <ArrowRight className="w-4 h-4 ml-2" />
                          </Link>
                        </Button>
                      </CardFooter>
                    </Card>

                  </div>
                </TabsContent>)}
            </div>
          </Tabs>
        </div>
      </div>
    </section>;
}